/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {getLocalStorage, setLocalStorage} from '@ion/core/src/capabilities/storage';
import {
  isCommonLoopbackIP as isSimulatedResellerDomain
} from '@ion/core/src/environment/environment';
import {getParameter} from '@ion/core/src/utils/QueryStringUtils';

/**
 * Defines the query string parameter which is used to provide the reseller
 * domain during development; e.g. http://127.0.0.1:8080?reseller=assistcargo
 *
 * Note: This function is to be considered internal as it is only intended to
 * be used by the package level `Reseller` API.
 */
const QUERY_PARAMETER = 'reseller';

/**
 * Defines the local storage key which is used to persist the reseller provided
 * by the query string upon initialization during local development. Persisting
 * the reseller value to localStorage ensures that it only needs to be provided
 * via the query string when the application first initializes, generally when
 * starting the resellers local dev server.
 *
 * Note: This function is to be considered internal as it is only intended to
 * be used by the package level `Reseller` API.
 */
const PERSISTENCE_KEY = 'ion-dev-tools-reseller';

/**
 * Provides a reference to the reseller / customer domain when running in local
 * development mode. In Development mode, the reseller domain is initially
 * provided by the application query string as `?reseller={customer}` whereby
 * the {customer} parameter matches a reseller directory name, such as the
 * 'assistcargo' directory in `ion-suite-resellers/public`.
 */
let _reseller: string;

/**
 * Provides a reference to the simulated reseller customer domain when running
 * in local development mode. In Development mode, the localhost loopback IP
 * address (i.e. http://127.0.0.1) is used exclusively to simulate a reseller
 * domain deployment, and the reseller being tested is initially provided in
 * the query string in the form of `?reseller={customer}`.
 *
 * Note: This function is to be considered internal as it is only intended to
 * be used by the package level `Reseller` API.
 */
export const getSimulatedReseller = (): string => {
  if (isSimulatedResellerDomain()) {
    return _reseller || getLocalStorage(PERSISTENCE_KEY) || getParameter(QUERY_PARAMETER);
  }
};

/**
 * Provides a utility which is to be invoked when the application is running in
 * a local development environment in order to ensure the reseller parameter is
 * parsed from the application query string.
 *
 * By default, the reseller domain is inferred from the host; however, during
 * local development, the reseller host needs to be simulated via the localhost
 * loopback IP address (127.0.0.0), and the reseller will be appended to the
 * query string, from there the value is persisted internally as well as to
 * localStorage so as to no longer rely on the value provided to the querystring
 * and to ensure if the query string has been removed and the page is refreshed
 * the value continues to be persisted.
 *
 * ```typescript
 *   // pages/_app.tsx
 *   import {isDevMode} from '@ion/applications/src/utils/env';
 *   import {initResellerDevTools} from '@ion/dls/src/theme/resellers';
 *   ...
 *
 *   if (isDevMode()) {
 *      initResellerDevTools();
 *   }
 * ```
 */
export const initResellerDevTools = () => {
  if (isSimulatedResellerDomain()) {
    const persisted = getLocalStorage(PERSISTENCE_KEY);
    _reseller = getParameter(QUERY_PARAMETER) || persisted;
    if (persisted !== _reseller) {
      setLocalStorage(PERSISTENCE_KEY, _reseller);
    }
  }
};
